import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, AlertCircle } from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import "../../styles/add-to-cart.css";

interface AddToCartButtonProps {
  productId: string;
  variationId?: string;
  disabled?: boolean;
  quantity?: number;
  selectedSize?: string;
  selectedColor?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  variant?: "default" | "icon" | "minimal";
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  productId,
  variationId,
  disabled = false,
  quantity = 1,
  selectedSize,
  selectedColor,
  onSuccess,
  onError,
  className = "",
  variant = "default",
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdatingQty, setIsUpdatingQty] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [quantityInCart, setQuantityInCart] = useState(0);
  const [cartItemId, setCartItemId] = useState<string | null>(null);
  const [activeVariationId, setActiveVariationId] = useState<
    string | undefined
  >(variationId);
  const { success } = useToast();
  const { t } = useTranslation();

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setActiveVariationId(variationId);
  }, [variationId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setQuantityInCart(0);
      setCartItemId(null);
      return;
    }

    const nextVariationId = variationId || activeVariationId;
    syncCartItem(nextVariationId);
  }, [variationId, selectedSize, selectedColor, productId, isAuthenticated]);

  const resolveVariationId = async (): Promise<string | undefined> => {
    if (variationId) return variationId;

    try {
      const productRes = await BaseApi.get(
        `/products/${productId}?isActive=true`,
      );
      const firstVariation = productRes?.data?.variations?.[0];
      return firstVariation?.id || firstVariation?._id;
    } catch (error) {
      console.error("Failed to resolve default variation:", error);
      return undefined;
    }
  };

  const matchesCartItem = (item: any, targetVariationId?: string): boolean => {
    if (!item || item.productId !== productId) return false;

    if (targetVariationId) {
      return item.variation?.id === targetVariationId;
    }

    if (selectedSize || selectedColor) {
      const itemSize = item.variation?.attributes?.size;
      const itemColor = item.variation?.attributes?.color;
      return (
        (selectedSize ? itemSize === selectedSize : true) &&
        (selectedColor ? itemColor === selectedColor : true)
      );
    }

    return true;
  };

  const syncCartItem = async (targetVariationId?: string) => {
    setIsSyncing(true);
    try {
      const res = await BaseApi.get("/cart");
      if (!res?.success || !res?.data?.items) return;

      const matched = res.data.items.find((item: any) =>
        matchesCartItem(item, targetVariationId),
      );

      if (matched) {
        setCartItemId(matched.id);
        setQuantityInCart(matched.quantity || 0);
        return matched;
      }
    } catch (error) {
      console.error("Failed to sync cart item:", error);
    } finally {
      setIsSyncing(false);
    }
    setCartItemId(null);
    setQuantityInCart(0);
    return null;
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/signin", { state: { from: location } });
      return;
    }

    if (isAdding || isUpdatingQty) return;

    setIsAdding(true);

    try {
      const resolvedVariationId = await resolveVariationId();
      setActiveVariationId(resolvedVariationId);
      const payload = {
        productId,
        quantity,
        ...(resolvedVariationId && { variationId: resolvedVariationId }),
        ...(selectedSize && { size: selectedSize }),
        ...(selectedColor && { color: selectedColor }),
      };

      const res = await BaseApi.post("/cart/items", payload);

      if (res.success) {
        const nextQuantity = Math.max(1, quantity);
        setQuantityInCart(nextQuantity);
        success(t("Product added to cart successfully!"));

        // Trigger cart badge animation
        window.dispatchEvent(
          new CustomEvent("cartUpdated", {
            detail: { action: "add", productId },
          }),
        );

        // Call success callback
        onSuccess?.();

        await syncCartItem(resolvedVariationId);
      } else {
        throw new Error(res.message || "Failed to add to cart");
      }
    } catch (error: any) {
      console.error("Failed to add to cart:", error);
      onError?.(error.message);
    } finally {
      setIsAdding(false);
    }
  };

  const updateQuantity = async (
    e: React.MouseEvent,
    delta: number,
  ): Promise<void> => {
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate("/signin", { state: { from: location } });
      return;
    }

    if (isUpdatingQty || isAdding) return;

    let currentItemId = cartItemId;
    if (!currentItemId) {
      const matched = await syncCartItem(activeVariationId);
      currentItemId = matched?.id || null;
    }

    if (!currentItemId) {
      if (delta > 0) {
        await handleAddToCart(e);
      }
      return;
    }

    const nextQuantity = Math.max(0, quantityInCart + delta);
    setQuantityInCart(nextQuantity);
    setIsUpdatingQty(true);

    try {
      if (nextQuantity === 0) {
        await BaseApi.delete(`/cart/items/${currentItemId}`);
        setCartItemId(null);
        window.dispatchEvent(
          new CustomEvent("cartUpdated", {
            detail: { action: "remove", productId },
          }),
        );
        return;
      }

      const res = await BaseApi.put(`/cart/items/${currentItemId}`, {
        quantity: nextQuantity,
      });

      if (!res.success) {
        throw new Error(res.message || "Failed to update quantity");
      }

      window.dispatchEvent(
        new CustomEvent("cartUpdated", {
          detail: { action: delta > 0 ? "add" : "decrease", productId },
        }),
      );
    } catch (error: any) {
      console.error("Failed to update cart quantity:", error);
      onError?.(error.message);
      await syncCartItem(activeVariationId);
    } finally {
      setIsUpdatingQty(false);
    }
  };

  // Icon-only variant
  const renderQuantityControls = (size: "icon" | "minimal" | "default") => (
    <div
      className={`add-to-cart-qty add-to-cart-qty--${size} add-to-cart-qty--roomy ${className}`}
    >
      <button
        type="button"
        className="add-to-cart-qty__btn"
        onClick={(e) => updateQuantity(e, -1)}
        disabled={isUpdatingQty}
        aria-label={t("Decrease quantity")}
      >
        -
      </button>
      <span className="add-to-cart-qty__value">{quantityInCart}</span>
      <button
        type="button"
        className="add-to-cart-qty__btn"
        onClick={(e) => updateQuantity(e, 1)}
        disabled={isUpdatingQty}
        aria-label={t("Increase quantity")}
      >
        +
      </button>
    </div>
  );

  const renderLoadingControls = (size: "icon" | "minimal" | "default") => (
    <div
      className={`add-to-cart-qty add-to-cart-qty--${size} add-to-cart-qty--roomy ${className}`}
    >
      <span className="loader-minimal"></span>
    </div>
  );

  if (isSyncing && quantityInCart === 0) {
    if (variant === "icon") {
      return renderLoadingControls("icon");
    }
    if (variant === "minimal") {
      return renderLoadingControls("minimal");
    }
    return renderLoadingControls("default");
  }

  if (quantityInCart > 0) {
    if (variant === "icon") {
      return renderQuantityControls("icon");
    }
    if (variant === "minimal") {
      return renderQuantityControls("minimal");
    }
    return renderQuantityControls("default");
  }

  if (variant === "icon") {
    return (
      <button
        onClick={handleAddToCart}
        disabled={isAdding || disabled}
        className={`add-to-cart-icon ${isAdding ? "adding" : ""} ${
          disabled ? "disabled" : ""
        } ${className}`}
        aria-label={t("Add to cart")}
      >
        <ShoppingCart size={20} />
      </button>
    );
  }

  // Minimal variant
  if (variant === "minimal") {
    return (
      <button
        onClick={handleAddToCart}
        disabled={isAdding || disabled}
        className={`add-to-cart-minimal ${isAdding ? "adding" : ""} ${className}`}
      >
        {isAdding ? <span className="loader-minimal"></span> : null}
        {!isAdding ? <ShoppingCart size={18} /> : null}
        {!isAdding ? <span>{t("Add")}</span> : null}
      </button>
    );
  }

  // Default full button variant
  return (
    <button
      onClick={handleAddToCart}
      disabled={isAdding || disabled}
      className={`add-to-cart-button ${isAdding ? "adding" : ""} ${
        disabled ? "disabled" : ""
      } ${className}`}
    >
      <span className="button-content">
        {isAdding ? (
          <>
            <span className="loader"></span>
            <span className="button-text">{t("Adding to Cart")}...</span>
          </>
        ) : disabled ? (
          <>
            <AlertCircle className="text-red-700" size={20} />
            <span className="text-red-700">{t("Out of Stock")}</span>
          </>
        ) : (
          <>
            <ShoppingCart size={20} />
            <span className="button-text">{t("Add to Cart")}</span>
          </>
        )}
      </span>
    </button>
  );
};

export default AddToCartButton;
